import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import {
  FileText, Clock, CheckCircle, AlertTriangle, X, Eye, 
  Loader2, AlertCircle, PenLine, Calendar, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

export default function PolicyAcknowledgementBanner() {
  const { user } = useContext(AuthContext);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [acknowledgeModalOpen, setAcknowledgeModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyContent, setPolicyContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [signature, setSignature] = useState('');
  const [comments, setComments] = useState('');
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    fetchPendingAcknowledgements();
  }, []);

  const fetchPendingAcknowledgements = async () => {
    try {
      const response = await axios.get(`${API}/policy-acknowledgements/pending`);
      setPending(response.data.pending || []);
    } catch (error) {
      console.error('Failed to fetch pending acknowledgements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPolicyContent = async (policyId) => {
    setLoadingContent(true);
    try {
      const response = await axios.get(`${API}/policies/${policyId}`);
      setPolicyContent(response.data);
    } catch (error) {
      console.error('Failed to fetch policy content:', error);
      toast.error('Failed to load policy content');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleOpenAcknowledge = async (ack) => {
    setSelectedPolicy(ack);
    setSignature('');
    setComments('');
    setAcknowledgeModalOpen(true);
    await fetchPolicyContent(ack.policy_id);
  };

  const handleAcknowledge = async () => {
    if (!signature.trim()) {
      toast.error('Please type your full name as your signature');
      return;
    }

    setAcknowledging(true);
    try {
      await axios.post(`${API}/policies/${selectedPolicy.policy_id}/acknowledge`, {
        signature: signature.trim(),
        comments: comments.trim() || null
      });
      
      toast.success('Policy acknowledged successfully');
      setAcknowledgeModalOpen(false);
      setSelectedPolicy(null);
      setPolicyContent(null);
      fetchPendingAcknowledgements();
    } catch (error) {
      console.error('Failed to acknowledge policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to acknowledge policy');
    } finally {
      setAcknowledging(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) return null;
  if (pending.length === 0) return null;

  const overdueCount = pending.filter(p => p.status === 'overdue').length;

  return (
    <>
      {/* Compact Banner */}
      <div 
        className={`${overdueCount > 0 ? 'bg-red-500' : 'bg-blue-500'} text-white px-4 py-3 cursor-pointer`}
        onClick={() => setExpanded(!expanded)}
        data-testid="policy-ack-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-white/20">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium">
                {pending.length} {pending.length === 1 ? 'Policy' : 'Policies'} Pending Acknowledgement
                {overdueCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {overdueCount} overdue
                  </span>
                )}
              </p>
              <p className="text-sm text-white/80">
                Click to review and acknowledge required policies
              </p>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {/* Expanded List */}
      {expanded && (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-4">
          <div className="max-w-7xl mx-auto space-y-3">
            {pending.map((ack) => {
              const daysUntilDue = getDaysUntilDue(ack.due_date);
              const isOverdue = ack.status === 'overdue' || daysUntilDue < 0;
              const isDueSoon = !isOverdue && daysUntilDue <= 3;
              
              return (
                <div 
                  key={ack.id}
                  className={`bg-white rounded-lg border p-4 flex items-center justify-between ${
                    isOverdue ? 'border-red-200' : isDueSoon ? 'border-amber-200' : 'border-slate-200'
                  }`}
                  data-testid={`pending-ack-${ack.policy_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      isOverdue ? 'bg-red-100' : isDueSoon ? 'bg-amber-100' : 'bg-blue-100'
                    }`}>
                      {isOverdue ? (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      ) : isDueSoon ? (
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{ack.policy_title}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                        {ack.policy_category && (
                          <span>{ack.policy_category}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Due: {formatDate(ack.due_date)}
                        </span>
                        {isOverdue && (
                          <span className="text-red-600 font-medium">Overdue</span>
                        )}
                        {isDueSoon && !isOverdue && (
                          <span className="text-amber-600 font-medium">Due soon</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleOpenAcknowledge(ack)}
                    className={isOverdue ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
                    data-testid={`acknowledge-btn-${ack.policy_id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Review & Acknowledge
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Acknowledge Modal */}
      <Dialog open={acknowledgeModalOpen} onOpenChange={setAcknowledgeModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedPolicy?.policy_title}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Please read the policy below and acknowledge that you have read and understood it.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Policy Info */}
            <div className="flex flex-wrap gap-4 text-sm">
              {policyContent?.category && (
                <div className="px-3 py-1 bg-slate-100 rounded-full text-slate-700">
                  {policyContent.category}
                </div>
              )}
              {policyContent?.version && (
                <div className="px-3 py-1 bg-slate-100 rounded-full text-slate-700">
                  Version {policyContent.version}
                </div>
              )}
              {selectedPolicy?.due_date && (
                <div className={`px-3 py-1 rounded-full flex items-center gap-1 ${
                  selectedPolicy.status === 'overdue' 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  <Clock className="w-3.5 h-3.5" />
                  Due: {formatDate(selectedPolicy.due_date)}
                </div>
              )}
            </div>

            {/* Policy Content */}
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 max-h-[300px] overflow-y-auto">
              {loadingContent ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : policyContent?.content ? (
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-slate-700">
                    {policyContent.content}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p className="mb-2">{policyContent?.description || 'No policy content available.'}</p>
                  {!policyContent?.content && (
                    <p className="text-sm">Please contact your administrator for the full policy document.</p>
                  )}
                </div>
              )}
            </div>

            {/* Acknowledgement Form */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <PenLine className="w-4 h-4" />
                Electronic Signature
              </h4>
              
              <div>
                <Label htmlFor="signature" className="text-blue-800">
                  Type your full name to acknowledge *
                </Label>
                <Input
                  id="signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={user?.name || "Your full name"}
                  className="mt-1 bg-white border-blue-200"
                  data-testid="signature-input"
                />
                <p className="text-xs text-blue-700 mt-1">
                  By typing your name, you confirm that you have read and understood this policy.
                </p>
              </div>

              <div>
                <Label htmlFor="comments" className="text-blue-800">
                  Comments (optional)
                </Label>
                <Textarea
                  id="comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Any questions or comments about this policy..."
                  className="mt-1 bg-white border-blue-200"
                  rows={2}
                  data-testid="comments-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAcknowledgeModalOpen(false)}
              className="border-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAcknowledge}
              disabled={acknowledging || !signature.trim()}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-acknowledge-btn"
            >
              {acknowledging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Acknowledging...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  I Acknowledge This Policy
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
